import React, { useEffect, useState, useMemo, useRef } from "react";
import { FastField, getIn } from "formik";
import { Calendar } from "react-multi-date-picker";
import configDefaultForm from "./configDefault";
import ErrorIcon from "./ErrorIcon";
import Required from "../form-component/Required";

import vi from "app/common/vi";

const GlobitsMultiDatePicker = (props) => (
  <FastField {...props} name={props.name} shouldUpdate={shouldComponentUpdate}>
    {({ field, meta, form }) => (
      <Component
        {...props}
        field={field}
        meta={meta}
        setFieldValue={form.setFieldValue}
        form={form}
      />
    )}
  </FastField>
);

const Component = ({
  name,
  label,
  required,
  requiredLabel,
  fullWidth = configDefaultForm.fullWidth,
  className = "",
  field,
  meta,
  debounceTime = configDefaultForm.debounceTime,
  notDelay,
  onChange,
  setFieldValue,
  readOnly,
  valueDefault,
  range = false,
  rangeHover = false,
  multiple = false,
  format = "YYYY-MM-DD",
  fieldName,
  form,
  fromField,
  toField,
  disabled = false,
  ...otherProps
}) => {
  const [value, setValue] = useState(field.value);
  const [timeoutId, setTimeoutId] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  useEffect(() => {
    if (fromField && toField && form) {
      let v0 = form.values?.[fromField];
      let v1 = form.values?.[toField];
      // Convert string ISO to Date object if needed
      if (typeof v0 === "string") v0 = v0 ? new Date(v0) : null;
      if (typeof v1 === "string") v1 = v1 ? new Date(v1) : null;
      setValue([v0, v1]);
      return;
    }
    if (Array.isArray(fieldName) && form) {
      let v0 = form.values?.[fieldName[0]];
      let v1 = form.values?.[fieldName[1]];
      if (typeof v0 === "string") v0 = v0 ? new Date(v0) : null;
      if (typeof v1 === "string") v1 = v1 ? new Date(v1) : null;
      if (v0 || v1) {
        setValue([v0, v1]);
        return;
      }
    }
    valueDefault ? setValue(valueDefault) : setValue(field.value);
  }, [field.value, valueDefault, fieldName, form, fromField, toField]);

  // Hàm format ngày theo format DD/MM/YYYY
  const formatDate = (date) => {
    if (!date) return "";
    if (date.format) return date.format(format);
    if (date instanceof Date) {
      const d = date.getDate().toString().padStart(2, "0");
      const m = (date.getMonth() + 1).toString().padStart(2, "0");
      const y = date.getFullYear();
      return `${d}/${m}/${y}`;
    }
    return "";
  };

  const getDisplayValue = () => {
    if (!value) return "";
    if (Array.isArray(value)) {
      //console.log("value", value);
      return value.map(formatDate).join(" - ");
    }
    return formatDate(value);
  };

  const handleChange = (val) => {
    setValue(val);

    if (fromField && toField && form) {
      if (Array.isArray(val)) {
        setFieldValue(fromField, val[0]?.toDate?.() || null);
        setFieldValue(toField, val[1]?.toDate?.() || null);
        if (onChange) onChange(val);
      }
      return;
    }

    if (Array.isArray(fieldName) && form) {
      if (Array.isArray(val)) {
        if (!notDelay) {
          if (timeoutId) clearTimeout(timeoutId);
          const id = setTimeout(() => {
            setFieldValue(fieldName[0], val[0]?.toDate?.() || null);
            setFieldValue(fieldName[1], val[1]?.toDate?.() || null);
            if (onChange) onChange(val);
          }, debounceTime);
          setTimeoutId(id);
        } else {
          setFieldValue(fieldName[0], val[0]?.toDate?.() || null);
          setFieldValue(fieldName[1], val[1]?.toDate?.() || null);
          if (onChange) onChange(val);
        }
      }
      return;
    }

    const getParsedValue = () => {
      if (Array.isArray(val)) {
        return val.map((v) => v?.toDate?.() || null);
      }
      return val?.toDate?.() || null;
    };

    const finalValue = getParsedValue();

    if (!notDelay) {
      if (timeoutId) clearTimeout(timeoutId);
      const id = setTimeout(() => {
        if (onChange) onChange(finalValue);
        else setFieldValue(name, finalValue);
      }, debounceTime);
      setTimeoutId(id);
    } else {
      if (onChange) onChange(finalValue);
      else setFieldValue(name, finalValue);
    }
  };

  const isError = meta?.touched && meta?.error;

  const displayLabel = useMemo(
    () =>
      label ? (
        <Required label={label} required={required || requiredLabel} />
      ) : null,
    [label, required, requiredLabel]
  );

  useEffect(() => {
    if (!showCalendar) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.multi-date-picker-wrapper-${name}`)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar, name]);

  return (
    <div
      className={`input-container ${className} multi-date-picker-wrapper-${name}`}
      style={{ position: "relative" }}
    >
      {displayLabel && (
        <label
          htmlFor={name}
          className=""
          style={{ display: "block", lineHeight: "1" }}
        >
          {displayLabel}
        </label>
      )}
      <div
        className="input-container-wrapper"
        style={{ position: "relative", width: "100%", marginTop: "3px" }}
      >
        <input
          type="text"
          disabled={disabled}
          readOnly
          value={getDisplayValue()}
          onClick={() => !readOnly && setShowCalendar(true)}
          className={`date-picker-input ${readOnly ? "read-only" : ""
            } responsive-padding `}
          style={{
            marginTop: "-1px",
            background: "#ffffff",
            border: "1px solid #4276a4",
            borderRadius: "3px",
            minHeight: "26px",
            height: "auto",
            width: "100%",
            cursor: readOnly ? "not-allowed" : "pointer",
            pointerEvents: readOnly ? "none" : "auto",
            boxSizing: "border-box",
          }}
          placeholder={label}
          {...otherProps.inputProps}
        />
        {/* Clear Icon */}
        {value && getDisplayValue() && !readOnly && (
          <span
            style={{
              position: "absolute",
              right: 32,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              zIndex: 2,
            }}
            onClick={(e) => {
              if (disabled) return;
              e.stopPropagation();
              setValue(range || multiple ? [] : null);
              if (fromField && toField && form) {
                setFieldValue(fromField, null);
                setFieldValue(toField, null);
              } else if (Array.isArray(fieldName) && form) {
                setFieldValue(fieldName[0], null);
                setFieldValue(fieldName[1], null);
              } else {
                setFieldValue(name, range || multiple ? [] : null);
              }
              if (onChange) onChange(range || multiple ? [] : null);
            }}
            tabIndex={-1}
            title="Clear"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              stroke="#888"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="5" x2="15" y2="15" />
              <line x1="15" y1="5" x2="5" y2="15" />
            </svg>
          </span>
        )}
        {/* Calendar Icon */}
        <span
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            cursor: readOnly ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            pointerEvents: readOnly ? "none" : "auto",
          }}
          onClick={() => {
            if (disabled) return;
            !readOnly && setShowCalendar(true)
          }}
          tabIndex={-1}
        >
          <svg
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <rect
              x="3"
              y="5"
              width="18"
              height="16"
              rx="2"
              fill="#fff"
              stroke="#888"
              strokeWidth="1.5"
            />
            <path
              d="M16 3v4M8 3v4"
              stroke="#888"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path d="M3 9h18" stroke="#888" strokeWidth="1.5" />
          </svg>
        </span>
      </div>
      {showCalendar && (
        <div
          style={{ position: "absolute", zIndex: 1000, top: "100%", left: 0 }}
        >
          <Calendar
            value={value}
            onChange={handleChange}
            readOnly={readOnly}
            range={range}
            rangeHover={rangeHover}
            multiple={multiple}
            format={format}
            locale={vi}
            className={`${readOnly ? "read-only" : ""} date-picker-input`}
            disabled={disabled}
            {...otherProps}
          />
        </div>
      )}
      {isError && fullWidth && (
        <div style={{ color: "#f44336", marginTop: 4 }}>{meta.error}</div>
      )}
      {isError && !fullWidth && <ErrorIcon helperText={meta.error} />}
    </div>
  );
};

const shouldComponentUpdate = (nextProps, currentProps) => {
  return (
    nextProps.name !== currentProps.name ||
    nextProps.value !== currentProps.value ||
    nextProps.onChange !== currentProps.onChange ||
    nextProps.label !== currentProps.label ||
    nextProps.required !== currentProps.required ||
    nextProps.disabled !== currentProps.disabled ||
    nextProps.readOnly !== currentProps.readOnly ||
    nextProps.fromField !== currentProps.fromField ||
    nextProps.toField !== currentProps.toField ||
    nextProps.formik.values[nextProps.fromField] !==
    currentProps.formik.values[currentProps.fromField] ||
    nextProps.formik.values[nextProps.toField] !==
    currentProps.formik.values[currentProps.toField] ||
    (nextProps.fieldName &&
      Array.isArray(nextProps.fieldName) &&
      (nextProps.formik.values[nextProps.fieldName[0]] !==
        currentProps.formik.values[currentProps.fieldName[0]] ||
        nextProps.formik.values[nextProps.fieldName[1]] !==
        currentProps.formik.values[currentProps.fieldName[1]])) ||
    Object.keys(nextProps).length !== Object.keys(currentProps).length ||
    getIn(nextProps.formik.values, currentProps.name) !==
    getIn(currentProps.formik.values, currentProps.name) ||
    getIn(nextProps.formik.errors, currentProps.name) !==
    getIn(currentProps.formik.errors, currentProps.name) ||
    getIn(nextProps.formik.touched, currentProps.name) !==
    getIn(currentProps.formik.touched, currentProps.name)
  );
};

export default React.memo(GlobitsMultiDatePicker);
